/**
 * Helpers for panel AI evaluation payloads.
 *
 * ``payload_json.dimensions`` is supposed to be an array, but older or
 * malformed model output can be an object keyed by dimension name. Using
 * ``(dimensions || []).map`` crashes when dimensions is a truthy non-array.
 */

import { isEvalUiStale } from '@/lib/evalErrors';

export function normalizePanelDimensions(dimensions) {
  if (Array.isArray(dimensions)) {
    return dimensions.filter((d) => d && typeof d === 'object');
  }
  if (dimensions && typeof dimensions === 'object') {
    return Object.entries(dimensions).map(([name, value]) => {
      if (value && typeof value === 'object') {
        return { name: value.name || name, ...value };
      }
      return { name, score: value };
    });
  }
  return [];
}

export function normalizePanelStringList(value) {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

/**
 * Staff AI-eval card state. In-flight (mutation or server pending/in_progress)
 * must win over a sticky failed status so Retry cannot be clicked again.
 */
export function resolvePanelEvalUiState({
  aiEvalStatus = null,
  aiEvalStartedAt = null,
  isPending = false,
} = {}) {
  const stale = aiEvalStatus === 'in_progress' && isEvalUiStale(aiEvalStartedAt);
  const inFlight = Boolean(isPending)
    || aiEvalStatus === 'pending'
    || (aiEvalStatus === 'in_progress' && !stale);
  const failed = !inFlight && (aiEvalStatus === 'failed' || stale);
  return { stale, inFlight, failed };
}
